import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2, Save } from "lucide-react";
import { DEFAULT_HOME, mergeHome } from "@/lib/homeContent";
import { ImageField } from "./ImageField";
import { IconPicker } from "./IconPicker";

const Row = ({ label, children }) => (
  <div className="grid sm:grid-cols-[160px_1fr] gap-2 sm:gap-4 items-start">
    <label className="text-[11px] font-mono uppercase tracking-widest text-slate-400 pt-2">{label}</label>
    <div>{children}</div>
  </div>
);

const inputCls = "bg-black/40 border-white/10 text-white";

const Text = ({ v, onChange, ...p }) => <Input value={v ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} {...p} />;
const Area = ({ v, onChange, ...p }) => <Textarea value={v ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} rows={3} {...p} />;

// Editor for a list of card objects. `cols` = which fields to show.
const ListEditor = ({ items = [], onChange, cols, section }) => {
  const set = (i, k, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: val } : it)));
  const add = () => onChange([...items, cols.reduce((a, c) => ({ ...a, [c.key]: c.type === "icon" ? "Atom" : "" }), {})]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="glass rounded-xl p-4 relative" data-testid={`${section}-item-${i}`}>
          <button onClick={() => remove(i)} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-rose-500/15 text-rose-400" data-testid={`${section}-item-del-${i}`}><Trash2 size={13} /></button>
          <div className="grid sm:grid-cols-2 gap-3 pr-8">
            {cols.map((c) => (
              <div key={c.key} className={c.type === "textarea" ? "sm:col-span-2" : ""}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{c.label}</div>
                {c.type === "icon" ? <IconPicker value={it[c.key] || "Atom"} onChange={(v) => set(i, c.key, v)} testid={`${section}-icon-${i}`} />
                  : c.type === "image" ? <ImageField value={it[c.key]} onChange={(v) => set(i, c.key, v)} testid={`${section}-img-${i}`} />
                  : c.type === "textarea" ? <Area v={it[c.key]} onChange={(v) => set(i, c.key, v)} />
                  : <Text v={it[c.key]} onChange={(v) => set(i, c.key, v)} />}
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add} data-testid={`${section}-add-item`}><Plus size={14} className="mr-2" />Add item</Button>
    </div>
  );
};

const Lines = ({ v = [], onChange, placeholder }) => (
  <Textarea value={(v || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").filter(Boolean))}
    className={inputCls} rows={3} placeholder={placeholder} />
);

export const HomeEditor = () => {
  const [c, setC] = useState(mergeHome({}));
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content/home").then(({ data }) => setC(mergeHome(data))).catch(() => setC(mergeHome({}))); }, []);

  const s = (section, patch) => setC((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content/home", c); toast.success("Home page updated — refresh the site to see it live"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const reset = () => { setC(mergeHome({})); toast.message("Reset to defaults (not yet saved)"); };

  const { hero, method, cases, pathway, protocols, science, tools } = c;

  return (
    <div data-testid="home-editor">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl text-white">Home Page</h1>
          <p className="text-xs text-slate-400 mt-1">Edit every section's text, icons and images. Changes go live on save.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={reset} data-testid="home-reset-btn">Reset defaults</Button>
          <Button onClick={save} disabled={saving} className="btn-primary rounded-lg" data-testid="home-save-btn"><Save size={15} className="mr-2" />{saving ? "Saving…" : "Save & Publish"}</Button>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["hero"]} className="space-y-3">
        <AccordionItem value="hero" className="glass rounded-2xl border-white/10 px-5" data-testid="section-hero">
          <AccordionTrigger className="text-white font-serif text-xl">1 · Hero</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Row label="Background"><ImageField value={hero.bg_image} onChange={(v) => s("hero", { bg_image: v })} testid="hero-bg" /></Row>
            <Row label="Badges"><Text v={(hero.badges || []).join(", ")} onChange={(v) => s("hero", { badges: v.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Peptides, Science, Longevity" /></Row>
            <Row label="Title"><Text v={hero.title} onChange={(v) => s("hero", { title: v })} placeholder="Use | for line breaks" /></Row>
            <Row label="Subtitle"><Area v={hero.subtitle} onChange={(v) => s("hero", { subtitle: v })} /></Row>
            <Row label="Primary CTA"><div className="flex gap-2"><Text v={hero.primary_label} onChange={(v) => s("hero", { primary_label: v })} placeholder="Label" /><Text v={hero.primary_link} onChange={(v) => s("hero", { primary_link: v })} placeholder="/link" /></div></Row>
            <Row label="Secondary CTA"><div className="flex gap-2"><Text v={hero.secondary_label} onChange={(v) => s("hero", { secondary_label: v })} placeholder="Label" /><Text v={hero.secondary_link} onChange={(v) => s("hero", { secondary_link: v })} placeholder="/link" /></div></Row>
            <Row label="Side words (left)"><Lines v={hero.sidewords_left} onChange={(v) => s("hero", { sidewords_left: v })} placeholder="One per line" /></Row>
            <Row label="Side words (right)"><Lines v={hero.sidewords_right} onChange={(v) => s("hero", { sidewords_right: v })} placeholder="One per line" /></Row>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="cases" className="glass rounded-2xl border-white/10 px-5" data-testid="section-cases">
          <AccordionTrigger className="text-white font-serif text-xl">2 · Peptide Cases</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Row label="Background"><ImageField value={cases.bg_image} onChange={(v) => s("cases", { bg_image: v })} testid="cases-bg" /></Row>
            <Row label="Eyebrow"><Text v={cases.eyebrow} onChange={(v) => s("cases", { eyebrow: v })} /></Row>
            <Row label="Title"><Text v={cases.title} onChange={(v) => s("cases", { title: v })} placeholder="Use | for line breaks" /></Row>
            <Row label="Intro"><Area v={cases.intro} onChange={(v) => s("cases", { intro: v })} /></Row>
            <Row label="CTA"><div className="flex gap-2"><Text v={cases.cta_label} onChange={(v) => s("cases", { cta_label: v })} placeholder="Label" /><Text v={cases.cta_link} onChange={(v) => s("cases", { cta_link: v })} placeholder="/link" /></div></Row>
            <Row label="Feature badges"><ListEditor section="cases" items={cases.badges} onChange={(v) => s("cases", { badges: v })}
              cols={[{ key: "icon", label: "Icon", type: "icon" }, { key: "label", label: "Label", type: "text" }]} /></Row>
            <p className="text-[11px] text-slate-500 font-mono">Case products shown in this section are managed under Products (type = case).</p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="pathway" className="glass rounded-2xl border-white/10 px-5" data-testid="section-pathway">
          <AccordionTrigger className="text-white font-serif text-xl">3 · Pathway</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Row label="Background"><ImageField value={pathway.bg_image} onChange={(v) => s("pathway", { bg_image: v })} testid="pathway-bg" /></Row>
            <Row label="Panel Title"><Text v={pathway.panel_title} onChange={(v) => s("pathway", { panel_title: v })} /></Row>
            <Row label="Title"><Text v={pathway.title} onChange={(v) => s("pathway", { title: v })} placeholder="Use | for line breaks" /></Row>
            <Row label="Subtitle"><Text v={pathway.subtitle} onChange={(v) => s("pathway", { subtitle: v })} /></Row>
            <Row label="Pathways"><ListEditor section="pathway" items={pathway.pathways} onChange={(v) => s("pathway", { pathways: v })}
              cols={[{ key: "icon", label: "Icon", type: "icon" }, { key: "category", label: "Category", type: "text" }, { key: "title", label: "Title", type: "text" }, { key: "desc", label: "Description", type: "textarea" }]} /></Row>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="protocols" className="glass rounded-2xl border-white/10 px-5" data-testid="section-protocols">
          <AccordionTrigger className="text-white font-serif text-xl">4 · Protocols</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Row label="Background"><ImageField value={protocols.bg_image} onChange={(v) => s("protocols", { bg_image: v })} testid="protocols-bg" /></Row>
            <Row label="Eyebrow"><Text v={protocols.eyebrow} onChange={(v) => s("protocols", { eyebrow: v })} /></Row>
            <Row label="Title"><Text v={protocols.title} onChange={(v) => s("protocols", { title: v })} /></Row>
            <Row label="Intro"><Area v={protocols.intro} onChange={(v) => s("protocols", { intro: v })} /></Row>
            <Row label="Items"><ListEditor section="protocols" items={protocols.items} onChange={(v) => s("protocols", { items: v })}
              cols={[{ key: "icon", label: "Icon", type: "icon" }, { key: "category", label: "Category", type: "text" }, { key: "image", label: "Image", type: "image" }, { key: "title", label: "Title", type: "text" }, { key: "desc", label: "Description", type: "textarea" }]} /></Row>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="science" className="glass rounded-2xl border-white/10 px-5" data-testid="section-science">
          <AccordionTrigger className="text-white font-serif text-xl">5 · Science</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Row label="Background"><ImageField value={science.bg_image} onChange={(v) => s("science", { bg_image: v })} testid="science-bg" /></Row>
            <Row label="Title"><Text v={science.title} onChange={(v) => s("science", { title: v })} placeholder="Use | for line breaks" /></Row>
            <Row label="Subtitle"><Text v={science.subtitle} onChange={(v) => s("science", { subtitle: v })} /></Row>
            <Row label="Items"><ListEditor section="science" items={science.items} onChange={(v) => s("science", { items: v })}
              cols={[{ key: "icon", label: "Icon", type: "icon" }, { key: "link", label: "Link", type: "text" }, { key: "title", label: "Title", type: "text" }, { key: "desc", label: "Description", type: "textarea" }]} /></Row>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="tools" className="glass rounded-2xl border-white/10 px-5" data-testid="section-tools">
          <AccordionTrigger className="text-white font-serif text-xl">6 · Instruments</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Row label="Eyebrow"><Text v={tools.eyebrow} onChange={(v) => s("tools", { eyebrow: v })} /></Row>
            <Row label="Title"><Text v={tools.title} onChange={(v) => s("tools", { title: v })} /></Row>
            <Row label="Items"><ListEditor section="tools" items={tools.items} onChange={(v) => s("tools", { items: v })}
              cols={[{ key: "title", label: "Title", type: "text" }, { key: "link", label: "Link", type: "text" }, { key: "desc", label: "Description", type: "textarea" }]} /></Row>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
